/**
 * tutorLiveActivityAdapter — l'activité d'un élève du live, telle que la sert l'API, vue du masteur au
 * centre de la salle.
 *
 * Portage des dérivations de `student/activity/todo/TodoModal.js` (branche tuteur, `noModal`), de
 * `components/ActivityItemInfo.js` (le titre, le chapitre, le chrono) et de
 * `common/liveMode/liveV3/TutorFocusLayoutM.js` (l'activité ouverte par défaut). L'énoncé et le rendu
 * reprennent les mappers de la fiche du parent (`activityDetailsAdapter`), même source (`TodoModal`), avec
 * les écarts du tuteur dits à leur place.
 *
 * Et les corps qui partent : ce que le legacy envoyait au serveur et à la salle pour chaque geste, à la clé
 * près (protocole identique, décision 7 du câblage).
 */
import {
    type ApiQuizLatestAttempt,
    type ApiQuizReviewQuestion,
} from "../../../../../engine/activity/prerequisite/quizReview"
import { type UIActivityStatement, type UIActivityStatus, type UIActivityWork } from "../../../ui/types"
import {
    activityDisplayStatus,
    toUIActivityStatement,
    toUICorrectionPoints,
    type ApiActivityDetails,
    type ApiActivityMaterial,
} from "../../parent/Activities/activityDetailsAdapter"

// ── Formes lâches (`ports/CLAUDE.md` § 5) ──────────────────────────────────────────────────────────

export type ApiLiveMaterial = ApiActivityMaterial & {
    statement?: string | null
    transcript?: string | null
    material_skills?: string | unknown[] | null
    material_key_skills?: unknown
    level?: number | null
    school_subject_id?: number | string | null
    class?: string | null
    structured_data?: { analysis?: { confidence?: string | null } | null } | null
    visual_explanation?: string[] | null
    figure_svg?: string | null
    correction_markdown?: string | null
    key_notions_markdown?: string | null
    correction_status?: string | null
}

export type ApiLiveQuizAttempt = ApiQuizLatestAttempt & { tutor_feedback?: number | null }

export type ApiLiveActivity = Omit<ApiActivityDetails, "material"> & {
    student_id?: number | string | null
    has_prerequisite_quiz?: boolean | number | null
    quiz_status?: string | null
    correction_status?: string | null
    correction_generating?: boolean | null
    prerequisite_quiz?: { id?: number | null; questions?: ApiQuizReviewQuestion[] | null } | null
    prerequisite_quiz_attempt?: ApiLiveQuizAttempt | null
    material?: ApiLiveMaterial | null
}

/** Un élève de la séance (`live_sessions[].users[]`). */
export type ApiLiveStudent = {
    id?: number | null
    firstname?: string | null
    display_name?: string | null
    todo_activities?: ApiLiveActivity[] | null
    activities?: ApiLiveActivity[] | null
    conversations?: { id?: number | null }[] | null
}

/** Deux ids égaux, l'un écrit en nombre, l'autre en chaîne ; jamais deux absents. */
export const sameId = (a: unknown, b: unknown) => a != null && b != null && String(a) === String(b)

// ── Le choix de l'activité ─────────────────────────────────────────────────────────────────────────

/** Les activités de l'élève : ses « à faire » puis ses faites, dans l'ordre de `TFL` et de `TodoModal`. */
export function liveStudentActivities(student: ApiLiveStudent | null | undefined): ApiLiveActivity[] {
    return [...(student?.todo_activities ?? []), ...(student?.activities ?? [])].filter(
        (activity): activity is ApiLiveActivity => activity?.id != null
    )
}

export function findLiveActivity(
    student: ApiLiveStudent | null | undefined,
    activityId: string | number | null | undefined
): ApiLiveActivity | null {
    if (activityId == null) return null
    return liveStudentActivities(student).find((activity) => sameId(activity.id, activityId)) ?? null
}

const lockedTime = (activity: ApiLiveActivity) => {
    const time = activity.locked_at != null ? Date.parse(activity.locked_at) : NaN
    return Number.isNaN(time) ? null : time
}

/**
 * L'activité qu'ouvre le centre quand on regarde un élève (`TutorFocusLayoutM.defaultSelectedActivityId`) :
 * celle qu'il a devant lui (l'attribut de salle `currentStudentActivity`), sinon la première de ses faites,
 * sinon sa « à faire » verrouillée le plus récemment. Elle doit être dans ses listes.
 *
 * Deux écarts voulus : le tri ne se fait plus **dans le store** (l'ancien triait `todo_activities` en place),
 * et une « à faire » sans `locked_at` passe après les verrouillées (l'ancien comparait des dates invalides,
 * d'ordre indéfini) ; sans aucune verrouillée, la première de la liste.
 *
 * « Si ce n'est pas celle-ci, celle-là » (daily du 04/11) : chaque candidate est essayée à son tour, et une
 * candidate qui n'est dans aucune de ses listes passe la main à la suivante, comme le `||` de l'ancien passait
 * sur le `0` que la salle écrit pour « aucune activité ». Avant, ce `0`, ou une activité pas encore relue,
 * arrêtait la recherche, et le centre tombait sur la visio.
 */
export function defaultLiveActivityId(
    student: ApiLiveStudent | null | undefined,
    currentActivityId: string | number | null | undefined
): string | null {
    if (student == null) return null
    const todos = (student.todo_activities ?? []).filter((activity) => activity?.id != null)
    const latestLocked = [...todos].sort((a, b) => (lockedTime(b) ?? -Infinity) - (lockedTime(a) ?? -Infinity))[0]
    for (const wanted of [currentActivityId, student.activities?.[0]?.id, latestLocked?.id]) {
        // Le `0` de la salle n'est dans aucune liste : il passe la main comme une activité pas encore relue.
        const found = findLiveActivity(student, wanted)
        if (found?.id != null) return String(found.id)
    }
    return null
}

// ── Ce que montre la tête ──────────────────────────────────────────────────────────────────────────

/** `ActivityItemInfo` : le titre du support, à défaut celui de l'activité. */
export function liveActivityTitle(raw: ApiLiveActivity | null | undefined): string {
    return raw?.material?.title ?? raw?.title ?? ""
}

/**
 * Le sous-titre de la tête : le chapitre, à défaut la matière, puis le chrono de `ActivityItemInfo`
 * (« 12 / 20 min », « - / 20 min » tant qu'il n'a pas de durée), quand l'activité a une durée prévue.
 */
export function liveActivityCaption(
    raw: ApiLiveActivity | null | undefined,
    subjectName: string | null | undefined
): string | undefined {
    const chapter = raw?.student_chapter?.chapter?.name
    const where = chapter != null && chapter !== "" ? chapter : (subjectName ?? undefined)
    const planned = raw?.estimated_duration
    const chrono =
        planned != null && planned !== 0
            ? `${raw?.duration ? `${Math.floor(raw.duration / 60)} / ` : "- / "}${planned} min`
            : undefined
    const parts = [where, chrono].filter((part): part is string => part != null && part !== "")
    return parts.length > 0 ? parts.join(" · ") : undefined
}

/** La pastille de statut de la tête (`TodoModal:746-756` + `ActivityItemInfo`), la règle de la fiche. */
export function liveActivityStatus(raw: ApiLiveActivity | null | undefined): UIActivityStatus | undefined {
    if (raw == null) return undefined
    return activityDisplayStatus(raw as ApiActivityDetails)
}

// ── Les vues de l'activité ─────────────────────────────────────────────────────────────────────────

/** L'énoncé, et la figure servie en SVG (`figure_svg`) que l'énoncé écrit dessine sous son texte. */
export function toUITutorLiveStatement(material: ApiLiveMaterial | null | undefined): UIActivityStatement {
    const statement = toUIActivityStatement(material)
    const svg = material?.figure_svg
    return svg != null && svg !== "" ? { ...statement, figureSvg: svg } : statement
}

/** `material_skills` non vide, chaîne ou liste (`TodoModal.hasMaterialSkills`). */
export function hasMaterialSkills(material: ApiLiveMaterial | null | undefined): boolean {
    const skills = material?.material_skills
    return typeof skills === "string" ? skills.trim().length > 0 : Array.isArray(skills) && skills.length > 0
}

/**
 * L'onglet « Tableau blanc » (`TodoModal.hasVisualExplanation`) : au centre du live, chez le tuteur, une
 * activité qui a ses compétences et un énoncé ou une transcription.
 */
export function liveActivityHasWhiteboard(raw: ApiLiveActivity | null | undefined): boolean {
    const material = raw?.material
    const filled = (text: string | null | undefined) => text != null && text.trim().length > 0
    return hasMaterialSkills(material) && (filled(material?.statement) || filled(material?.transcript))
}

/**
 * Le rendu, vu du tuteur. Les journaux (`step === 2`, `step_media_sent`) donnent les photos, la correction
 * en première image (`TodoModal:1200-1203`). Écart avec la fiche du parent : **les points de la correction
 * se montrent au tuteur dès qu'ils existent**, sans garde sur l'image (`showStructuredCorrection`, C-74),
 * et la copie à pastilles quand chaque point a son ancre (`anchoredCorrection`).
 */
export function toUITutorLiveWork(
    raw: ApiLiveActivity | null | undefined,
    cleanUrl: (url: string | null | undefined) => string | null
): UIActivityWork | null {
    if (raw == null || (raw.media_count ?? 0) <= 0) return null
    const logs = raw.activity_logs ?? []
    const mediaLogs = logs.filter((log) => log?.step === 2 && log?.action === "step_media_sent")
    if (raw.category === "write") {
        const workImages = mediaLogs
            .map((log) => log?.work_attachment)
            .filter((uri): uri is string => uri != null && uri !== "")
        const correction = raw.correction_image
        const hasCorrection = correction != null && correction !== ""
        const points = toUICorrectionPoints(raw.correction_points, workImages.length)
        const anchored = points.length > 0 && workImages.length > 0 && points.every((point) => point.anchor != null)
        return {
            images: hasCorrection ? [correction, ...workImages] : workImages,
            hasCorrection,
            correctionPoints: points.length > 0 ? points : undefined,
            copyPages: anchored ? workImages : undefined,
        }
    }
    const chronoLog = logs.find((log) => log?.step === 2 && log?.action === "step_finished")
    const audioUri = cleanUrl(mediaLogs[0]?.work_attachment)
    return {
        images: [],
        audioUri: audioUri ?? undefined,
        audioDurationSeconds: chronoLog?.step_data?.chrono_duration ?? undefined,
    }
}

/** Il y a un rendu (`hasRendu`) : le défaut de l'onglet et « Soumettre » s'en servent. */
export function liveActivityHasWork(raw: ApiLiveActivity | null | undefined): boolean {
    return (raw?.media_count ?? 0) > 0
}

/** L'avis du tuteur sur le quiz, déjà donné (`prerequisite_quiz_attempt.tutor_feedback`, 1 ou -1). */
export function liveQuizTutorFeedback(raw: ApiLiveActivity | null | undefined): 1 | -1 | null {
    const value = raw?.prerequisite_quiz_attempt?.tutor_feedback
    return value === 1 || value === -1 ? value : null
}

// ── L'aide à la correction (`MaterialCorrectionDisplay`) ───────────────────────────────────────────

export type LiveAidStatus = "generating" | "ready" | "failed" | null

/** `correctionStatusOf` : le statut du serveur, à défaut le marqueur « ... » d'une correction en cours. */
export function liveAidStatus(material: ApiLiveMaterial | null | undefined): LiveAidStatus {
    if (material?.correction_status !== undefined) {
        const status = material.correction_status
        return status === "generating" || status === "ready" || status === "failed" ? status : null
    }
    if (material?.correction_markdown === "...") return "generating"
    return material?.correction_markdown ? "ready" : null
}

/** Le corrigé montré : les notions clés puis la correction (`MaterialCorrectionDisplay`). */
export function liveAidMarkdown(material: ApiLiveMaterial | null | undefined): string {
    return [material?.key_notions_markdown, material?.correction_markdown]
        .filter((part): part is string => part != null && part !== "")
        .join("\n\n## Correction \n\n")
}

// ── Les corps qui partent ──────────────────────────────────────────────────────────────────────────

/**
 * « Soumettre » (`TodoModal:913-931`) : la durée du chrono, et la clé temporaire d'une pièce pas encore
 * confirmée s'il y en a une (`buildPendingAttachmentsBody`, passée par l'appelant).
 */
export function finalizeSilentBody(
    raw: ApiLiveActivity,
    pendingAttachments: unknown[] | null | undefined
): Record<string, unknown> {
    const body: Record<string, unknown> = { chrono_duration: raw.duration || 0 }
    if (pendingAttachments != null && pendingAttachments.length > 0) body.pending_attachments = pendingAttachments
    return body
}

type ApiFinalizeAnswer = {
    activity?: {
        xp_gained?: number | null
        finished_at?: string | null
        status?: string | null
        duration?: number | null
    }
    skill_sets?: {
        current_count?: number | null
        is_last_evolution_read?: boolean | null
        current_step?: number | null
        steps?: { petals_gained?: number | null }[] | null
    }[]
}

/** Les pétales gagnés : ceux des compétences revenues à zéro et pas encore lues (`TodoModal:534-548`). */
export function finalizedPetals(answer: ApiFinalizeAnswer | null | undefined): number {
    return (answer?.skill_sets ?? [])
        .filter((set) => set?.current_count === 0 && !set?.is_last_evolution_read)
        .reduce((sum, set) => sum + (set?.steps?.[(set?.current_step ?? 0) - 1]?.petals_gained || 0), 0)
}

/** Le message `tutorFinalizedActivity` à l'élève, champ pour champ (`TodoModal:575-592`). */
export function tutorFinalizedActivityPayload(
    studentId: number,
    raw: ApiLiveActivity,
    answer: ApiFinalizeAnswer | null | undefined
): Record<string, unknown> {
    const finalized = answer?.activity
    return {
        studentId,
        activityId: raw.id,
        activityTitle: raw.material?.title ?? raw.title,
        xpGained: finalized?.xp_gained ?? 0,
        petals: finalizedPetals(answer),
        finishedAt: finalized?.finished_at,
        status: finalized?.status,
        duration: finalized?.duration,
        xpGainedActivity: finalized?.xp_gained,
    }
}

/**
 * « Redonner » (`TodoModal.onPress`, branche tuteur) : la même activité, à nouveau à faire. Le modèle et
 * la matière sont ceux que le catalogue connaît (`findItemInListWithId`), absents sinon.
 */
export function redoLiveActivityBody(
    raw: ApiLiveActivity,
    known: { activityTemplateId?: string | number | null; schoolSubjectId?: string | number | null }
): Record<string, unknown> {
    const chapter = raw.student_chapter?.chapter
    return {
        activity_template_id: known.activityTemplateId ?? undefined,
        school_subject_id: known.schoolSubjectId ?? undefined,
        material_id: raw.material?.id,
        material_chapter: raw.chapter,
        estimated_duration: raw.estimated_duration,
        chapter_id: chapter?.id,
        chapter_name: chapter?.name,
        chapter_class: chapter?.class,
    }
}

/** La dernière conversation de l'élève avec le champion (`student.conversations[length - 1]`). */
export function lastConversationId(student: ApiLiveStudent | null | undefined): number | null {
    const conversations = student?.conversations ?? []
    return conversations[conversations.length - 1]?.id ?? null
}
